import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Check, Minus, RotateCcw, TriangleAlert } from "lucide-react";

import { Badge } from "@/design-system/components/Badge/Badge";
import { Button } from "@/design-system/components/Button/Button";
import {
  Confetti,
  type ConfettiHandle,
} from "@/design-system/components/Confetti/Confetti";
import { Drawer } from "@/design-system/components/Drawer/Drawer";
import { IconButton } from "@/design-system/components/IconButton/IconButton";
import { Typography } from "@/design-system/components/Typography/Typography";
import {
  getAdjacentStepId,
  getApplicableSteps,
  resolveCurrentStepId,
  sessionHasProgress,
} from "./navigation";
import {
  AnyWizardDefinition,
  WizardCompletionSummary,
  WizardSession,
  WizardStepProps,
} from "./types";
import { WizardErrorBoundary } from "./WizardErrorBoundary";
import { useWizards } from "./WizardProvider";
import {
  WizardReset,
  WizardResetConfirmation,
} from "./WizardResetConfirmation";
import { WizardStepFrameContext } from "./WizardStepLayout";
import { WizardStepRail } from "./WizardStepRail";

type WizardPanelProps = {
  definition: AnyWizardDefinition;
  session: WizardSession;
};

export function WizardPanel({ definition, session }: WizardPanelProps) {
  const {
    openWizard,
    minimizeWizard,
    discardWizard,
    restartWizard,
    updateWizardData,
    goToWizardStep,
    completeWizard,
    recordApplicableSteps,
  } = useWizards();
  const [pendingReset, setPendingReset] = useState<WizardReset | null>(null);
  const stepContext = definition.useStepContext(session.data);
  const sessionId = session.id;

  const applicableSteps = useMemo(
    () => getApplicableSteps(definition.steps, session.data, stepContext),
    [definition.steps, session.data, stepContext],
  );

  useEffect(() => {
    recordApplicableSteps(
      sessionId,
      applicableSteps.map((step) => step.id),
    );
  }, [applicableSteps, recordApplicableSteps, sessionId]);
  const currentStepId =
    resolveCurrentStepId(
      definition.steps,
      applicableSteps,
      session.currentStepId,
    ) ?? definition.steps[0].id;
  const currentIndex = applicableSteps.findIndex(
    (step) => step.id === currentStepId,
  );
  const currentStep = applicableSteps[currentIndex];
  const isCompleted = session.completedAt !== null;

  const goNext = useCallback(() => {
    const nextStepId = getAdjacentStepId(applicableSteps, currentStepId, 1);
    if (nextStepId) {
      goToWizardStep(sessionId, nextStepId, currentStepId);
    } else {
      completeWizard(sessionId, currentStepId);
    }
  }, [
    applicableSteps,
    currentStepId,
    goToWizardStep,
    completeWizard,
    sessionId,
  ]);

  const goBack = useCallback(() => {
    const previousStepId = getAdjacentStepId(
      applicableSteps,
      currentStepId,
      -1,
    );
    if (previousStepId) {
      goToWizardStep(sessionId, previousStepId);
    }
  }, [applicableSteps, currentStepId, goToWizardStep, sessionId]);

  const stepProps: WizardStepProps<object, unknown> = {
    data: session.data,
    context: stepContext,
    updateData: (patch) => updateWizardData(sessionId, patch),
    goNext,
    goBack,
    goToStep: (stepId) => goToWizardStep(sessionId, stepId),
    minimize: minimizeWizard,
    complete: () => completeWizard(sessionId, currentStepId),
  };

  const hasProgress = sessionHasProgress(definition, session);

  function handleCloseButtonClick() {
    if (isCompleted || !hasProgress) {
      discardWizard(sessionId);
    } else {
      setPendingReset("discard");
    }
  }

  function confirmReset() {
    if (pendingReset === "restart") {
      restartWizard(sessionId);
    } else if (pendingReset === "discard") {
      discardWizard(sessionId);
    }
    setPendingReset(null);
  }

  return (
    <>
      <Drawer
        title={definition.title}
        subtitle={definition.getSubtitle?.(session.data)}
        open
        placement="bottom"
        onOpenChange={(open) => {
          if (!open) minimizeWizard();
        }}
        onCloseButtonClick={handleCloseButtonClick}
        closeButtonLabel={isCompleted ? "Close wizard" : "Discard wizard"}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          (event.currentTarget as HTMLElement | null)?.focus();
        }}
        headerActions={
          <div className="flex items-center gap-1">
            {hasProgress && !isCompleted && (
              <Button
                variant="ghost"
                size="sm"
                leadingIcon={<RotateCcw aria-hidden />}
                onClick={() => setPendingReset("restart")}
              >
                Start over
              </Button>
            )}
            <IconButton
              aria-label="Minimize"
              tooltipContent="Minimize. Your progress is saved."
              onClick={minimizeWizard}
            >
              <Minus className="size-icon-md" strokeWidth={2} aria-hidden />
            </IconButton>
          </div>
        }
      >
        <div className="flex min-h-0 flex-1">
          <WizardStepRail
            mascot={definition.mascot}
            steps={applicableSteps}
            session={session}
            currentStepId={currentStepId}
            onSelectStep={(stepId) => goToWizardStep(sessionId, stepId)}
          />
          {isCompleted ? (
            <WizardCompleted
              summary={
                definition.getCompletionSummary?.(
                  session.data,
                  stepContext,
                ) ?? {
                  title: "All done",
                  description: `You finished “${definition.title}”.`,
                }
              }
              stepCount={applicableSteps.length}
              celebrate={
                session.completedAt !== null &&
                Date.now() - session.completedAt < CELEBRATION_WINDOW_MS
              }
              onClose={() => discardWizard(sessionId)}
              onStartAnother={() => {
                discardWizard(sessionId);
                openWizard(definition.type);
              }}
            />
          ) : (
            currentStep && (
              <WizardStepFrameContext.Provider
                value={{
                  stepNumber: currentIndex + 1,
                  stepCount: applicableSteps.length,
                  canGoBack: currentIndex > 0,
                  goBack,
                  goNext,
                }}
              >
                <WizardErrorBoundary
                  key={currentStep.id}
                  wizardType={definition.type}
                  stepId={currentStep.id}
                  fallback={(retry) => <WizardStepError onRetry={retry} />}
                >
                  <currentStep.Component {...stepProps} />
                </WizardErrorBoundary>
              </WizardStepFrameContext.Provider>
            )
          )}
        </div>
      </Drawer>
      <WizardResetConfirmation
        pending={pendingReset}
        onCancel={() => setPendingReset(null)}
        onConfirm={confirmReset}
      />
    </>
  );
}

function WizardStepError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center">
      <TriangleAlert
        className="text-status-warning size-icon-xl"
        strokeWidth={1.5}
        aria-hidden
      />
      <Typography variant="heading-md" as="h2">
        This step couldn’t load
      </Typography>
      <Typography variant="body-md" color="subtle">
        Your progress is saved. Try again, or pick another step.
      </Typography>
      <Button variant="outline" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}

const CELEBRATION_WINDOW_MS = 5_000;

function WizardCompleted({
  summary,
  stepCount,
  celebrate,
  onClose,
  onStartAnother,
}: {
  summary: WizardCompletionSummary;
  stepCount: number;
  celebrate: boolean;
  onClose: () => void;
  onStartAnother: () => void;
}) {
  const confettiRef = useRef<ConfettiHandle>(null);

  useEffect(() => {
    if (celebrate) {
      confettiRef.current?.celebrate();
    }
    // Only celebrate when the completion screen first appears.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center">
      <Confetti ref={confettiRef} />
      <span className="bg-status-success-tint text-status-success animate-bounce-in flex size-12 items-center justify-center rounded-full">
        <Check className="size-icon-lg" strokeWidth={2.5} aria-hidden />
      </span>
      <div className="flex max-w-md flex-col gap-1">
        <Typography variant="heading-lg" as="h2">
          {summary.title}
        </Typography>
        <Typography variant="body-md" color="subtle">
          {summary.description}
        </Typography>
      </div>
      <div className="flex flex-wrap justify-center gap-2">
        <Badge
          variant="success"
          text={`${stepCount} ${stepCount === 1 ? "step" : "steps"} completed`}
        />
        {summary.highlights?.map((highlight) => (
          <Badge key={highlight} variant="success" text={highlight} />
        ))}
      </div>
      <div className="flex gap-2 pt-2">
        <Button variant="outline" onClick={onStartAnother}>
          Start another
        </Button>
        <Button onClick={onClose}>Close</Button>
      </div>
    </div>
  );
}
